import React, { ReactNode } from 'react';
import { Option, Select } from '@saucelabs/ds-inputs';
import { Typography } from '@saucelabs/ds-typography';
import { Col, Row } from '@saucelabs/sl-component-grid';
import { IconApiTestingOutline, IconDevicePhoneOutline } from '@saucelabs/ds-icons';

import { Plan } from '../../records/Plan';
import {
  ALLOWED_PROJECTS_VALUES,
  INTERVAL_KEY,
  PLAN_INTERVAL,
  PRODUCT_API_TESTING,
  PRODUCT_MOBILE_QUALITY,
} from '../../constants';
import { PlanPurchaseCard, PlanPurchaseCardProps } from '../PlanPurchaseCard';
import Product from '../Purchase/Product';
import { projectsToMonthlyExecutions } from '../../utils/plans';
import { amountSeparator, formatThousand } from '../../utils/numbers';

type SelectedPlanChoice = typeof PRODUCT_API_TESTING | typeof PRODUCT_MOBILE_QUALITY;

const renderPlansSelectedItem = (selectedItem, interval): ReactNode => {
  if (!selectedItem) {
    return 'Select Plan';
  }

  return (
    <Typography
      className={selectedItem.rendered[0].props.className}
      component={selectedItem.rendered[0].props.component}
      variant="body-large"
    >
      {selectedItem.rendered[0].props.children}
    </Typography>
  );
};

export interface Props {
  handleSubscribe: (SelectedPlanChoice) => void;
  localPlansConfig: { interval: PlanInterval, projects: number };
  setLocalPlansConfig?:
  ({ interval, projects }: { interval: PlanInterval, projects: number }) => void;
}

export interface APITestingProps extends Props, Partial<PlanPurchaseCardProps> {
  apiTestingPlans: { [interval: string]: Plan };
}

export interface MobileQualityProps extends Props, Partial<PlanPurchaseCardProps> {
  mobileQualityPlans: { [interval: string]: Plan };
}

export interface MoreTestingProps extends APITestingProps, MobileQualityProps {}

export const ApiTestingCard: React.FC<APITestingProps> = ({
  apiTestingPlans,
  handleSubscribe,
  localPlansConfig,
  setLocalPlansConfig,
  ...props
}) => {
  const localApiTestingConfig = localPlansConfig[PRODUCT_API_TESTING];
  const selectedPlan = apiTestingPlans[localApiTestingConfig.interval];

  return (
    <PlanPurchaseCard
      billingPeriod={localApiTestingConfig.interval}
      dropdown={selectedPlan && (
        <Select
          className="mt-1 mb-0.25"
          data-qa="api-testing-purchase-dropdown"
          defaultSelectedKey={String(ALLOWED_PROJECTS_VALUES[0])}
          onSelectionChange={(val) => setLocalPlansConfig({
            ...localPlansConfig,
            [PRODUCT_API_TESTING]: {
              ...localApiTestingConfig,
              projects: Number(val),
            },
          })}
          renderSelectedItem={renderPlansSelectedItem}
          selectionMode="single"
        >
          {ALLOWED_PROJECTS_VALUES.map((projects: number) => (
            <Option
              key={`${projects}`}
              data-qa={`api-testing-purchase-option-${projects}`}
            >
              <Typography
                className="text-high-emphasis mr-0.5"
                component="span"
                variant="body-small"
              >
                {projects}
                {' projects, '}
                {formatThousand(projectsToMonthlyExecutions(projects, selectedPlan.add_ons))}
                {' executions'}
              </Typography>
              {' '}
              <Typography
                className="text-medium-emphasis"
                component="span"
                variant="body-small"
              >
                {amountSeparator(selectedPlan.calculateMonthlyRate(projects))}
                {'/month'}
              </Typography>
            </Option>
          ))}
        </Select>
      )}
      forceMonthlyPricing={localApiTestingConfig.interval === PLAN_INTERVAL.YEAR}
      handleContinue={() => handleSubscribe(PRODUCT_API_TESTING)}
      heading="API Testing"
      icon={IconApiTestingOutline}
      includes={[
        'Detailed error reporting',
        'Global visibility',
        'API monitoring',
      ]}
      rate={selectedPlan.calculateMonthlyRate(localApiTestingConfig.projects)}
      seeAllFeaturesLink="https://saucelabs.com/products/api-testing"
      taglines={[
        'Functional and Integration Testing',
      ]}
      {...props}
    />
  );
};

const ErrorReportingCard: React.FC = () => (
  <PlanPurchaseCard
    continueText="Get started with Backtrace for free"
    handleContinue={() => { window.open('https://backtrace.io/pricing', '_blank'); }}
    heading="Error and Crash Reporting"
    includes={[
      'Debugging and bug tracking',
      'App monitoring',
      'Error monitoring',
    ]}
    rate={0}
    seeAllFeaturesLink="https://saucelabs.com/products/error-reporting"
    taglines={[
      'Monitor and capture every critical event',
      'Customizable analysis and prioritization',
    ]}
  />
);

const LowCodeCard: React.FC = () => (
  <PlanPurchaseCard
    continueText="Request a Demo"
    handleContinue={() => { window.open('https://saucelabs.com/request-demo', '_blank'); }}
    heading="Low-Code Testing"
    includes={[
      'Intelligent test management',
      'Auto-maintain assets',
      'Regulatory compliance',
    ]}
    seeAllFeaturesLink="https://saucelabs.com/products/low-code-testing"
    taglines={[
      'Autonomously create test cases and scripts',
      'Get real-time feedback for long-term scalability',
    ]}
  />
);

export const MobileQualityCard: React.FC<MobileQualityProps> = ({
  handleSubscribe,
  localPlansConfig,
  mobileQualityPlans,
  ...props
}) => {
  const localMobileQualityConfig = localPlansConfig[PRODUCT_MOBILE_QUALITY];
  const selectedPlan = mobileQualityPlans[localMobileQualityConfig.interval];

  return (
    <PlanPurchaseCard
      billingPeriod={localMobileQualityConfig.interval}
      forceMonthlyPricing={localMobileQualityConfig.interval === PLAN_INTERVAL.YEAR}
      handleContinue={() => handleSubscribe(PRODUCT_MOBILE_QUALITY)}
      heading="Mobile Beta Testing"
      icon={IconDevicePhoneOutline}
      includes={[
        '50 Apps, 500 Beta Testers',
        '180 Days Data Retention',
        '10,000 video session recordings per month',
      ]}
      rate={selectedPlan.calculateMonthlyRate(1)}
      seeAllFeaturesLink="https://saucelabs.com/products/mobile-testing/app-betas"
      taglines={[
        'App Distribution',
        'In-App Feedback',
      ]}
      {...props}
    />
  );
};

export const MoreTestingPlans: React.FC<MoreTestingProps> = ({
  apiTestingPlans,
  handleSubscribe,
  localPlansConfig,
  mobileQualityPlans,
  setLocalPlansConfig,
}) => {
  const currentInterval = (localPlansConfig[PRODUCT_API_TESTING][INTERVAL_KEY] ||
    localPlansConfig[PRODUCT_MOBILE_QUALITY][INTERVAL_KEY]) as PlanInterval;

  const changeInterval = (isMonthly: boolean) => {
    const desiredInterval = isMonthly ? PLAN_INTERVAL.MONTH : PLAN_INTERVAL.YEAR;
    setLocalPlansConfig({
      ...localPlansConfig,
      [PRODUCT_API_TESTING]: {
        ...localPlansConfig[PRODUCT_API_TESTING],
        [INTERVAL_KEY]: desiredInterval,
      },
      [PRODUCT_MOBILE_QUALITY]: {
        ...localPlansConfig[PRODUCT_MOBILE_QUALITY],
        [INTERVAL_KEY]: desiredInterval,
      },
    });
  };

  return (
    <>
      <Row>
        <Col md={12}>
          <div className="mb-2 mt-5 flex items-center justify-between" >
            <Typography
              className="text-high-emphasis mt-1"
              disableSpacing
              id="more-plans"
              variant="heading-2"
            >
              More testing plans
            </Typography>
            <Product.IntervalChoice
              id="usage-side-plans-interval-switch"
              onChange={changeInterval}
              saving="20%"
              selectedInterval={currentInterval}
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <div
            className="grid grid-cols-2 gap-2 items-start"
            data-qa="additional-plans-cards"
          >
            <MobileQualityCard
              handleSubscribe={handleSubscribe}
              localPlansConfig={localPlansConfig}
              mobileQualityPlans={mobileQualityPlans}
            />
            <ApiTestingCard
              apiTestingPlans={apiTestingPlans}
              handleSubscribe={handleSubscribe}
              localPlansConfig={localPlansConfig}
              setLocalPlansConfig={setLocalPlansConfig}
            />
          </div>
        </Col>
      </Row>
    </>
  );
};
export default MoreTestingPlans;
